import styled from "styled-components";
import {color} from 'styled-system';
import Link from 'next/link';

import IndicatorMiniCart from "./IndicatorMiniCart";
import IndicatorWishlist from "./IndicatorWishlist";
import IndicatorLogin from "./IndicatorLogin";
import {useSession} from "next-auth/react";
import IndicatorAccount from "./IndicatorAccount";
import IndicatorMenu from "./IndicatorMenu";
import SearchAutocomplete from "./SearchAutocomplete";
import {useRouter} from "next/router";
import {getNavPath} from "../../shared/navigateUtil";
import IndicatorCountryLang from "./IndicatorCountryLang";
import {useContext, useEffect} from "react";
import {LoggedOutCartContext} from "../../contexts/LoggedOutCartContext";
import {getLocalStorageItem} from "../../shared/localStorageUtil";
import {LoggedOutWishlistContext} from "../../contexts/LoggedOutWishlistContext";
import {ClientContext} from "../../contexts/ClientContext";
import {useClientContext} from "../../hooks/useContext";
import {useAtom} from 'jotai';
import {nextSessionAtom} from "../../store/sessionAtom"
import {getTheme} from "../../shared/themeUtils";

const HeaderElement = styled.div`
    height: 80px;
    background-color: ${getTheme("colors.white")};
    color: ${getTheme("colors.primary")};
    position: sticky;
    box-shadow: 0 5px 10px -3px rgba(0, 0, 0, 0.1);
    top: 0;
    z-index: 10;

    @media (max-width: ${getTheme("breakpoints.1")}) {
        height: auto;
        padding: ${getTheme("space.1")} 0;
    }
`;


const Container = styled.div`
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: ${getTheme("colors.primary")};

    .logo {
        height: auto;
        margin-right: ${getTheme("space.2")};
        cursor: pointer;
        display: flex;
        align-items: center;

        .logo-img {
            width: 120px;
        }
    }

    @media (max-width: ${getTheme("breakpoints.1")}) {
        .logo .logo-img {
            width: 90px;
        }

        .menu, .search {
            display: none !important;
        }
    }
`;


const MobileMenu = styled.div`
    display: none;

    @media (max-width: ${getTheme("breakpoints.1")}) {
        display: flex;
        justify-content: space-between;
        align-items: center;
        .search {
            width: 100%;
            margin-top: ${getTheme("space.1")};
            margin-right: ${getTheme("space.2")};
            margin-bottom: ${getTheme("space.0")};
        }
    }
`

const Content = styled.div`
    width: 50%;
`


const LeftSide = styled(Content)`
    display: flex;
    align-content: center;
    width: 100%;

    .logo-link {
        display: flex;
        align-items: center;
    }

    .menu, .country-lang {
        display: flex;
        align-items: center;
        margin-right: ${getTheme("space.3")};
    }

    .search {
        margin: 0 auto;
        width: 100%;
    }

    @media (max-width: ${getTheme("breakpoints.1")}) {

        .menu, .country-lang {
            margin-right: ${getTheme("space.2")};
        }

        .search {
            margin: 0;
            width: ${getTheme("space.3")};
        }
    }
`


const RightSide = styled(Content)`
    display: flex;
    justify-content: flex-end;
    ${color};
    max-width: 350px;
`


const UserNavigation = styled.div`
    ul {
        padding: 0;
        margin: 0;
        display: flex;
        justify-content: flex-end;

        li {
            list-style: none;
            margin-left: ${getTheme("space.3")};
            display: flex;
        }
    }

    @media (max-width: ${getTheme("breakpoints.1")}) {

        ul {
            li {
                margin-left: ${getTheme("space.1")};
            }
        }
    }
`

export default function Header() {
  const {data: session, status} = useSession();
  const [sessionAtom, setSessionAtom] = useAtom(nextSessionAtom)
  const router = useRouter()
  const {setLoggedOutCartCtx} = useContext(LoggedOutCartContext)
  const {setLoggedOutWishlistCtx} = useContext(LoggedOutWishlistContext)
  const contextQuery = useClientContext()
  const {setClientCtx} = useContext(ClientContext)

  useEffect(() => {
    setSessionAtom({session, status})
  }, [session, status])

  useEffect(() => {

    // populate context with persisted localStorage value of loggedOutCart
    const persistedLogoutCart = getLocalStorageItem("loggedOutCart");
    if (persistedLogoutCart != null && persistedLogoutCart.length > 0) {
      setLoggedOutCartCtx(JSON.parse(persistedLogoutCart))
    }

    // populate context with persisted localStorage value of loggedOutWishlist
    const persistedLoggedOutWishlist = getLocalStorageItem("loggedOutWishlist")
    if (persistedLoggedOutWishlist != null && persistedLoggedOutWishlist.length > 0) {
      setLoggedOutWishlistCtx(JSON.parse(persistedLoggedOutWishlist))
    }

    if (contextQuery.data) {
      setClientCtx(contextQuery.data)
    }
  }, [contextQuery.data])

  return (
    <>
      <HeaderElement>
        <Container className="appContainer">
          <LeftSide>
            <Link href={getNavPath(router, "/")} className={"logo-link"}>
              <div className="logo">
                {contextQuery.data && contextQuery.data.divisionCode === "mus" && <img
                  src="/client-images/logos/musix_logo.svg"
                  alt="MusiX Logo"
                  className={"logo-img"}
                />}
                {contextQuery.data && contextQuery.data.divisionCode === "ven" && <img
                  src="/client-images/logos/venoa_logo.png"
                  alt="Venoa Logo"
                  className={"logo-img"}
                />}
              </div>
            </Link>

            <div className="country-lang" data-test="country-lang">
              <IndicatorCountryLang/>
            </div>

            <div className="menu" data-test="menu">
              <IndicatorMenu/>
            </div>

            <div className="search" data-test="search">
              <SearchAutocomplete/>
            </div>

          </LeftSide>

          <RightSide>
            <UserNavigation>
              <ul>
                {!session?.user && <li data-test="login"><IndicatorLogin/></li>}
                {session?.user && <li data-test="account"><IndicatorAccount/></li>}
                <li><IndicatorWishlist/></li>
                <li><IndicatorMiniCart/></li>
              </ul>
            </UserNavigation>
          </RightSide>
        </Container>

        <MobileMenu className="appContainer">
          <div className="search">
            <SearchAutocomplete isMobileSearch={true}/>
          </div>

          <div className="menu">
            <IndicatorMenu/>
          </div>
        </MobileMenu>
      </HeaderElement>
    </>
  );
}
